import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { formatCycleTime, formatDate, formatDuration } from 'lib/utils/date';
import _startCase from 'lodash/startCase';
import { formatValue } from 'lib/utils/text';
import { getRemainingOperationDuration } from 'pages/SelectJob/SelectWorkOrderOp/JobCard/helpers';
import moment from 'moment';
import useFetchJobSubmittedParts from 'lib/hooks/useFetchJobSubmittedParts';
import styled from 'styled-components';
import { palette, Icon } from '@m12s/component-library';
import { PCM_MAX_VALUE } from 'lib/constants/partCountMultiplier';
import { edit } from 'lib/icons';

import WorkOrderOpMachineName from 'components/WorkOrderOp/MachineName';
import { withDashFallback } from 'pages/SelectJob/JobInfoERP/helpers';
import {
  CardContainer,
  CardHead,
  CardHeader,
  CardBody,
  CardFoot,
  CardBodyRow,
  LargeText,
  MediumHeaderText,
  MediumText,
  TextContainer,
  DescriptionRow,
} from './styled';

const PartCountMethodRow = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  text-align: left;
  color: inherit;
`;

const formatPartCountMultiplierDisplay = (partCountMultiplier) => {
  if (!partCountMultiplier || partCountMultiplier <= 0)
    return '1 cycle makes 1 part';
  if (partCountMultiplier >= 1) {
    const parts = Math.min(Math.round(partCountMultiplier), PCM_MAX_VALUE);
    return `1 cycle makes ${parts} part${parts === 1 ? '' : 's'}`;
  }
  const cycles = Math.min(Math.round(1 / partCountMultiplier), PCM_MAX_VALUE);
  return `${cycles} cycle${cycles === 1 ? '' : 's'} make 1 part`;
};

export const DURATION_FORMAT = 'short';
const DURATION_OPTIONS = { trim: 'both' };

const SelectedJobSummary = ({
  workOrderOp,
  partCountMultiplier,
  onEditPartCountMultiplier,
  isPromptEnabled,
}) => {
  const { t } = useTranslation();

  const {
    scheduledStartDate,
    workOrder,
    finishQuantity,
    setupTimeMs,
    cycleTimeMs,
    matchingPartOperations,
  } = workOrderOp || {};

  const expectedSetupTime = setupTimeMs / 1000;
  const expectedCycleTime = cycleTimeMs / 1000;

  const [requestJobSubmittedParts, { parts }] = useFetchJobSubmittedParts();

  useEffect(() => {
    if (workOrderOp) {
      requestJobSubmittedParts(workOrderOp);
    }
  }, [requestJobSubmittedParts, workOrderOp]);

  const orderProgressText = [parts?.totalParts, finishQuantity]
    .map((data) => {
      return formatValue(data)?.toLocaleString() || '-';
    })
    .join(' / ');

  if (!workOrderOp) {
    return null;
  }

  const remainingOperationDuration = getRemainingOperationDuration({
    operation: workOrderOp,
    currentOperation: workOrderOp,
  });

  const durationText =
    moment
      .duration(remainingOperationDuration)
      .format('h[h] m[m] s[s]', { largest: 2 }) || '0s';

  const lotSplitSubValue = [
    withDashFallback(workOrder?.lot),
    withDashFallback(workOrder?.split),
    withDashFallback(workOrder?.sub),
  ].join(' | ');

  return (
    <CardContainer>
      <CardHead>
        <CardHeader>
          <CardBodyRow columns={2}>
            <TextContainer>
              <MediumHeaderText>{t('Expected setup')}</MediumHeaderText>
              <LargeText>
                {expectedSetupTime
                  ? formatDuration(
                      expectedSetupTime,
                      DURATION_FORMAT,
                      DURATION_OPTIONS
                    )
                  : _startCase(t('none'))}
              </LargeText>
            </TextContainer>

            <TextContainer>
              <MediumHeaderText>{t('Expected cycle time')}</MediumHeaderText>
              <LargeText>
                {formatCycleTime(expectedCycleTime, DURATION_OPTIONS)}
              </LargeText>
            </TextContainer>
          </CardBodyRow>
        </CardHeader>
      </CardHead>
      <CardBody>
        <CardBodyRow columns={2}>
          <TextContainer>
            <MediumHeaderText>{t('Order due date')}</MediumHeaderText>
            <MediumText>{formatDate(workOrder?.dueDate)}</MediumText>
          </TextContainer>

          <TextContainer>
            <MediumHeaderText>{t('Planned start date')}</MediumHeaderText>
            <MediumText>{formatDate(scheduledStartDate)}</MediumText>
          </TextContainer>
        </CardBodyRow>
        <CardBodyRow columns={2}>
          <TextContainer>
            <MediumHeaderText>{t('Estimated duration')}</MediumHeaderText>
            <MediumText>
              {cycleTimeMs ? durationText || 'N/A' : 'No cycle time'}
            </MediumText>
          </TextContainer>

          <TextContainer>
            <MediumHeaderText>{t('Lot/Split/Sub')}</MediumHeaderText>
            <MediumText>{lotSplitSubValue}</MediumText>
          </TextContainer>

          <TextContainer>
            <MediumHeaderText>{t('Resource')}</MediumHeaderText>
            <MediumText>
              <WorkOrderOpMachineName workOrderOp={workOrderOp} />
            </MediumText>
          </TextContainer>
        </CardBodyRow>

        {isPromptEnabled && (
          <CardBodyRow columns={1}>
            <TextContainer>
              <MediumHeaderText>{t('Part count method')}</MediumHeaderText>
              <PartCountMethodRow
                onClick={onEditPartCountMultiplier}
                aria-label="Edit part count multiplier"
              >
                <MediumText>
                  {formatPartCountMultiplierDisplay(partCountMultiplier)}
                </MediumText>
                <Icon icon={edit} size="1rem" color={palette.Indigo600} />
              </PartCountMethodRow>
            </TextContainer>
          </CardBodyRow>
        )}

        {matchingPartOperations?.[0]?.description && (
          <DescriptionRow columns={1}>
            <MediumText>{matchingPartOperations?.[0]?.description}</MediumText>
          </DescriptionRow>
        )}
      </CardBody>
      <CardFoot>
        <LargeText>{orderProgressText}</LargeText>
      </CardFoot>
    </CardContainer>
  );
};

SelectedJobSummary.propTypes = {
  workOrderOp: PropTypes.object,
  partCountMultiplier: PropTypes.number,
  onEditPartCountMultiplier: PropTypes.func,
  isPromptEnabled: PropTypes.bool,
};

export default SelectedJobSummary;
